'use client';

import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import type { ContactFormProps } from '@routeroyal/theme-core';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';

/**
 * Stone Investment Contact Form
 * 
 * Elegant form with:
 * - Minimal styling
 * - Square corners
 * - Sophisticated typography
 */
export function StoneInvestmentContactForm({ 
  recipientEmail: _recipientEmail, 
  fields = ['name', 'email', 'phone', 'message'],
  onSubmit,
  messages,
}: ContactFormProps & { messages?: Record<string, unknown> }) {
  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const formMsg = (stoneMessages.contactForm || {}) as Record<string, string>;

  const [formData, setFormData] = useState<Record<string, string>>({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const formStartedRef = useRef(false);

  const handleFormFocus = () => {
    if (!formStartedRef.current) {
      formStartedRef.current = true;
      analyticsEvents.formStart('contact_form');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (onSubmit) {
        await onSubmit(formData);
      }
      setIsSubmitted(true);
      setFormData({ name: '', email: '', phone: '', message: '' });

      analyticsEvents.formSubmit('contact_form', window.location.pathname);
      analyticsEvents.generateLead('contact_form');

      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'form_submission',
          form_name: 'contact_form',
          form_location: window.location.pathname,
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-12"
      >
        <div className="w-16 h-16 border border-[var(--color-text)] mx-auto mb-6 flex items-center justify-center">
          <svg className="w-8 h-8 text-[var(--color-text)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 
          className="text-2xl text-[var(--color-text)] mb-4"
          style={{ 
            fontFamily: "'Cormorant Garamond', 'Georgia', serif",
            fontWeight: 400,
          }}
        >
          {formMsg.successTitle || 'Thank you for your message'}
        </h3>
        <p 
          className="text-[var(--color-text-muted)]"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          {formMsg.successMessage || 'We will get back to you shortly.'}
        </p>
      </motion.div>
    );
  }

  const inputClassName = `w-full bg-transparent border-b border-[var(--color-border)] py-4 text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-text)] focus:outline-none transition-colors duration-300`;

  return (
    <form onSubmit={handleSubmit} onFocusCapture={handleFormFocus} className="space-y-8">
      {fields.includes('name') && (
        <div>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder={formMsg.namePlaceholder || formMsg.nameLabel || 'Your Name'}
            required
            className={inputClassName}
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          />
        </div>
      )}
      
      {fields.includes('email') && (
        <div>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={formMsg.emailPlaceholder || formMsg.emailLabel || 'Email Address'}
            required
            className={inputClassName}
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          />
        </div>
      )}
      
      {fields.includes('phone') && (
        <div>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder={formMsg.phonePlaceholder || formMsg.phoneLabel || 'Phone Number'}
            className={inputClassName}
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          />
        </div>
      )}
      
      {fields.includes('message') && (
        <div>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder={formMsg.messagePlaceholder || formMsg.messageLabel || 'Your Message'}
            rows={4}
            required
            className={`${inputClassName} resize-none`}
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          />
        </div>
      )}
      
      <button
        type="submit"
        disabled={isSubmitting}
        className="inline-flex items-center gap-4 px-8 py-4 bg-[var(--color-text)] text-[var(--color-background)] text-xs tracking-[0.15em] uppercase transition-all duration-300 hover:bg-[var(--color-text)]/90 disabled:opacity-50"
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      >
        <span>{isSubmitting ? (formMsg.sending || 'Sending...') : (formMsg.submit || 'Send Message')}</span>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
        </svg>
      </button>
    </form>
  );
}
